import type { ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils/cn'

interface LoadingStateProps {
  label?: string
  className?: string
  /** `inline` = spinner + texto; `table`/`cards`/`form` = skeleton com o formato do conteúdo. */
  variant?: 'inline' | 'table' | 'cards' | 'form'
  rows?: number
  columns?: number
  count?: number
}

/** Estado de carregamento padronizado (sempre anunciado para leitores de tela). */
export function LoadingState({
  label = 'Carregando…',
  className,
  variant = 'inline',
  rows = 6,
  columns = 6,
  count = 6,
}: LoadingStateProps) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className={className}>
      <span className="sr-only">{label}</span>
      {variant === 'inline' ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          <span aria-hidden>{label}</span>
        </div>
      ) : null}
      {variant === 'table' ? <TableRowsSkeleton rows={rows} columns={columns} /> : null}
      {variant === 'cards' ? <CardsSkeleton count={count} /> : null}
      {variant === 'form' ? <FormSkeleton rows={rows} /> : null}
    </div>
  )
}

export function TableRowsSkeleton({ rows = 8, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" aria-hidden>
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="flex gap-3">
            {Array.from({ length: columns }).map((__, colIndex) => (
              <Skeleton key={colIndex} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export function CardsSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" aria-hidden>
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index}>
          <CardHeader>
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-40" />
          </CardHeader>
          <CardContent className="space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-5/6" />
            <Skeleton className="h-3 w-4/6" />
            <Skeleton className="h-7 w-40" />
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

export function FormSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <Card aria-hidden>
      <CardContent className="space-y-5">
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className="space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

/** Tela cheia usada antes do shell existir (login, callback, configuração). */
export function FullPageState({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex min-h-full items-center justify-center bg-background p-6', className)}>
      <div className="w-full max-w-lg">{children}</div>
    </div>
  )
}
